
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import { Loader } from '@googlemaps/js-api-loader'
import boosterHydrate from '../node_modules/nuxt-booster/runtime/hydrate.mjs'

export default Vue.extend({
  name: 'Radar',
  components: {
    RadarFilters: boosterHydrate(() => import('../components/RadarFilters.vue')),
    RadarList: boosterHydrate(() => import('../components/RadarList.vue')),
    ErrorModal: boosterHydrate(() => import('~/components/modals/ErrorModal.vue')),
    SelectButton: boosterHydrate(() => import('primevue/selectbutton')),
    InputText: boosterHydrate(() => import('primevue/inputtext')),
    Button: boosterHydrate(() => import('primevue/button')),
    Dropdown: boosterHydrate(() => import('primevue/dropdown'))
  },
  beforeRouteLeave(to, _from, next) {
    if (to && !to.name.includes('radar') && !to.name.includes('application-id-steps') && !to.name.includes('login')) {
      this.setCity('')
      this.setSearchTitle('')
    }
    next()
  },
  layout: 'radar-footer',
  data() {
    return {
      isFirstEnter: true,
      $sanitizeTitle: null,
      $gtm: null,
      isDropdownOpen: false,
      resizeTimeout: null,
      selectedOrder: '1',
      orders: [{
        name: this.$t('radar.recent'),
        value: '0'
      },
      {
        name: this.$t('radar.featuredCompanies'),
        value: '1'
      }],
      selectedFilter: [],
      filters: [
        {
          name: this.$t('radar.fullTime'),
          correlation_id: 3,
          type: 'workingHour'
        },
        {
          name: this.$t('radar.partTime'),
          correlation_id: 4,
          type: 'workingHour'
        },
        {
          name: this.$t('radar.fullRemote'),
          correlation_id: 1,
          type: 'remoteWorking'
        },
        {
          name: this.$t('radar.referral'),
          correlation_id: 1,
          type: 'orp'
        }
      ],
      radarSearch: {
        text: '',
        city: null
      },
      filteredCities: [],
      service: null,
      servicePlaces: null,
      isLoadingPlaces: false,
      timer: null,
      showFilters: false,
      advancedFilters: {
        city: null,
        ea: '0',
        contracts: [],
        skills: [],
        education: null,
        vertical: null,
        radius: null,
        roles: [],
        seniorities: [],
        languages: [],
        remoteWorking: [],
        workingHours: [],
        protectedCategory: null,
        travel: null,
        benefits: []
      },
      verified: false,
      orp: false,
      radarOverflow: false,
      index_start: 0,
      country_iso_code: this.$i18n.locale,
      limit: 20,
      count: 0,
      pages: 1,
      visiblePages: [],
      $isEmptyObject: null,
      popupError: false,
      error: {},
      lat: 0,
      lon: 0,
      routeChanged: false,
      meta: null,
      showMain: false,
      retries: 0,
      isFetching: false,
      isProgrammaticScroll: false,
      hasCampaign: !!this.$route.query.id
    }
  },
  async fetch() {
    const page = this.$route.query.p !== undefined ? parseInt(this.$route.query.p.toString()) : 1
    if (this.$route.query.id) {
      await this.loadCampaign(Number(this.$route.query.id))
    }
    await this.loadPage(page)
  },
  head() {
    const isProduction = process.env.BUILD_ENV === 'production'
    const hasSearch = this.$route.query.search !== undefined
    const index = isProduction && !hasSearch
    const city = this.$route.params.city
    const capitalizeWords = str =>
      str
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

    const title = city
      ? `${capitalizeWords(city)} - ${this.$t('menu.radar')}`
      : this.$t('menu.radar').toString()

    this.meta = title
    const description = this.$route.params.city ? this.$t('radar.descriptionCity', { city: this.$route.params.city.split(' ').map(w => w.substring(0, 1).toUpperCase() + w.substring(1))?.join(' ') }).toString() : this.$t('menu.radar').toString()
    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'robots', name: 'robots', content: index ? 'index,follow' : 'noindex,follow' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0, user-scalable=yes, minimum-scale=1.0, maximum-scale=5.0' },
        { name: 'apple-mobile-web-app-capable', content: 'yes' },
        { name: 'mobile-web-app-capable', content: 'yes' },
        { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' }
      ]
    }
  },
  computed: {
    ...mapGetters({
      isMobile: 'site/isMobile',
      posts: 'radar/getPosts',
      currentPage: 'radar/getCurrentPage',
      campaign: 'campaign/getCampaign',
      googleKey: 'radar/getGoogleKey',
      getLat: 'radar/getLat',
      getLon: 'radar/getLon',
      getCity: 'radar/getCity',
      getSearchTitle: 'radar/getSearchTitle'
    }),
    advancedFiltersSelected() {
      const { ea, contracts, skills, roles, seniorities, languages, remoteWorking, workingHours, benefits, education, vertical, radius, protectedCategory, travel } = this.advancedFilters
      return ea !== '0' ||
           contracts.length || skills.length || roles.length ||
           seniorities.length || languages.length ||
           remoteWorking.length || workingHours.length ||
           benefits.length ||
           education || vertical || (radius && radius !== '15000') ||
           protectedCategory || travel
    },
    routeName() {
      return this.$route.params.city !== undefined ? 'radar-city' : 'radar'
    }
  },
  watch: {
    '$route.query': '$fetch',
    $route: {
      handler() {
        this.routeChanged = true
        this.hasCampaign = !!this.$route.query.id
      },
      deep: true,
      immediate: true
    }
  },
  mounted() {
    this.$dayjs.locale(this.$i18n.locale)
    try {
      const loader = new Loader({
        apiKey: this.googleKey,
        language: this.$i18n.locale,
        version: 'weekly',
        libraries: ['places']
      })
      loader.load().then((google) => {
        this.service = new google.maps.places.AutocompleteService()
        const map = new google.maps.Map(document.createElement('div'))
        this.servicePlaces = new google.maps.places.PlacesService(map)
      })
    } catch (err) {
      if (err.toString().includes('Loader must not be called again')) {
        console.log('reload api')
      } else {
        console.log(err.toString())
      }
    }
  },
  methods: {
    ...mapActions({
      setCampaign: 'campaign/setCampaign',
      setPosts: 'radar/setPosts',
      setCurrentPage: 'radar/setCurrentPage',
      setLat: 'radar/setLat',
      setLon: 'radar/setLon',
      setCity: 'radar/setCity',
      setSearchTitle: 'radar/setSearchTitle'
    }),
    async loadCampaign(cid) {
      if (this.$auth.loggedIn && this.$auth.user.uid !== 0) {
        const params = {
          params: {
            cid,
            uid: this.$auth.user.uid,
            caller: 'candidates',
            locale: this.$i18n.locale
          }
        }

        try {
          const response = this.$route.query.verified === 'true' || this.$route.query.verified === undefined ? await this.$axios.$get('/proxyapi/auth/get-campaign-by-cid-v3', params) : await this.$axios.$post('/proxyapi/auth/get-campaign-by-cid-v2-aggr', params)
          const campaign = response
          this.setCampaign(campaign)
        } catch (err) {
          this.$nuxt.error({ statusCode: err.response.status, message: 'Campaign not found' })
        }
      } else {
        const params = {
          params: {
            cid,
            caller: 'candidates',
            locale: this.$i18n.locale
          }
        }

        try {
          const response = this.$route.query.verified === 'true' || this.$route.query.verified === undefined ? await this.$axios.get('/proxyapi/get-campaign-by-cid-v3', params) : await this.$axios.$post('/proxyapi/get-campaign-by-cid-v2-aggr', params)
          const campaign = response.data
          this.setCampaign(campaign)
        } catch (err) {
          this.$nuxt.error({ statusCode: err.response.status, message: 'Campaign not found' })
        }
      }
    },
    track(eventName, text, city, ea, orp, campaignsCount) {
      this.$gtm.push({
        event: eventName,
        event_data: {
          search_term_1: text,
          search_term_2: city,
          search_term_3: Number(ea) === 0 ? this.$t('advancedFilters.allJobs') : Number(ea) === 1 ? this.$t('advancedFilters.companies') : this.$t('advancedFilters.agencies'),
          search_orp: orp,
          search_result: campaignsCount && campaignsCount > 0,
          page_url: this.$router.currentRoute.fullPath,
          page_title: this.meta

        }
      })
    },
    handleClick(newFilters) {
      this.selectedFilter = newFilters
      const filterTypes = ['workingHour', 'remoteWorking']
      filterTypes.forEach((type) => {
        const key = type === 'workingHour' ? 'workingHours' : 'remoteWorking'
        this.advancedFilters[key] = this.advancedFilters[key].filter(
          af => !this.filters.some(
            filter => filter.type === type && Number(filter.correlation_id) === Number(af.correlation_id)
          )
        )
      })
      newFilters.forEach((chip) => {
        if (chip.type === 'workingHour' || chip.type === 'remoteWorking') {
          const key = chip.type === 'workingHour' ? 'workingHours' : 'remoteWorking'
          this.advancedFilters[key].push({ correlation_id: chip.correlation_id })
        }
      })

      // Aggiorna lo stato di `orp`
      this.orp = newFilters.some(chip => chip.type === 'orp')
      this.search()
    },
    handleSuggested() {
      const orpFilter = this.filters.find(chip => chip.type === 'orp')
      this.orp = !this.selectedFilter.some(chip => chip.type === 'orp')
      if (this.orp && orpFilter) {
        this.selectedFilter.push(orpFilter)
      } else {
        this.selectedFilter = this.selectedFilter.filter(chip => chip.type !== 'orp')
      }
      this.search()
    },
    closeFilters() {
      this.showFilters = false
    },
    resetFilters() {
      this.advancedFilters = {
        city: null,
        ea: '0',
        contracts: [],
        skills: [],
        education: null,
        vertical: null,
        radius: null,
        roles: [],
        seniorities: [],
        languages: [],
        remoteWorking: [],
        workingHours: [],
        protectedCategory: null,
        travel: null,
        benefits: []
      }
      this.orp = false

      this.search()
    },
    updateAndSearch(obj) {
      if (obj.close) {
        this.showFilters = false
      }
      this.advancedFilters = obj.filters
      if (this.advancedFilters.city) {
        this.radarSearch.city = this.advancedFilters.city
        this.setCity(this.radarSearch.city.formatted_locality)
        this.setLat(this.radarSearch.city.lat)
        this.setLon(this.radarSearch.city.lon)
      } else {
        this.radarSearch.city = null
        this.setCity('')
        this.setLat(0)
        this.setLon(0)
      }
      this.search()
    },
    updateText() {
      if (this.radarSearch?.text === '') {
        this.search()
      }
    },
    search() {
      const formattedParams = this.sanitizeParams(false, 1)
      this.setCurrentPage(-1)
      if (this.radarSearch?.text) {
        this.setSearchTitle(this.$sanitizeTitle(this.radarSearch?.text))
      } else {
        this.setSearchTitle('')
      }
      if (!this.radarSearch?.city) {
        this.$router.push(this.localePath({ name: 'radar', query: formattedParams }))
      } else {
        delete formattedParams.city
        this.$router.push(this.localePath({ name: 'radar-city', params: { city: this.$sanitizeTitle(this.radarSearch?.city?.formatted_locality) }, query: formattedParams }))
      }
    },
    addSelectedPlaces(e) {
      this.servicePlaces.getDetails(
        {
          placeId: e.place_id,
          types: '(cities)',
          fields: ['geometry.location']
        },
        (place, serviceStatus) => {
          this.createGoogleLocations(place, serviceStatus)
        }
      )
    },
    createGoogleLocations(place, serviceStatus) {
      if (serviceStatus === 'OK' && place?.geometry?.location) {
        const location = place.geometry.location
        const formattedLocality = this.radarSearch.city?.formatted_locality || ''

        if (formattedLocality) {
          this.radarSearch.city.lat = location.lat()
          this.radarSearch.city.lon = location.lng()
          this.advancedFilters.city = this.radarSearch.city
          this.setCity(formattedLocality)
          this.setLat(this.radarSearch.city.lat)
          this.setLon(this.radarSearch.city.lon)
        } else {
          console.error('Errore: formatted_locality non definito o vuoto', formattedLocality)
        }
      } else {
        console.error('Errore nel recupero dei dettagli del luogo', serviceStatus, place)
      }
    },
    update(loc) {
      clearTimeout(this.timer)
      if (loc && loc.length > 0) {
        this.isLoadingPlaces = true
        this.timer = setTimeout(() => {
          this.advancedFilters.radius = '15000'
          this.service.getPlacePredictions(
            {
              input: loc,
              types: ['administrative_area_level_3']
            },
            this.displaySuggestions
          )
        }, 750)
      }
    },
    displaySuggestions(predictions, status) {
      if (status !== 'OK') {
        this.filteredCities = []
        this.isLoadingPlaces = false
        return
      }

      // Filtra e aggiungi le città in un'unica operazione
      const filtred = predictions
        .filter(pred => this.radarSearch?.city?.place_id !== pred.place_id)
        .map((p) => {
          if (p) {
            p.formatted_locality_complete = p?.description
            p.formatted_locality = p.structured_formatting.main_text
          }
          return p
        })

      this.isLoadingPlaces = false
      this.filteredCities = filtred

      return predictions
    },
    getMedia(url, date) {
      const parts = url.split('/')
      const updatedUrl = parts.slice(4).join('/')
      return '/' + updatedUrl + '?updateTs=' + date
    },
    updateFirstCampaign(posts) {
      console.log('update first')
      if (!this.campaign) { return posts } // Se la campagna non esiste, restituisci direttamente i post

      if (!Array.isArray(posts.campaigns)) {
        posts.campaigns = []
      }
      // Rimuovo la campagna corrente dai posts
      posts.campaigns = posts.campaigns.filter(campaign => Number(campaign.cid) !== Number(this.campaign.cid))
      const currentCampaign = { ...this.campaign, count: posts.campaigns[0]?.count || 0 }
      posts.campaigns.unshift(currentCampaign)

      return posts
    },
    sanitizeParams(isFromFetch, page) {
      if (!page) {
        page = 1
      }
      const limit = this.$route.query.limit ? Number(this.$route.query.limit) : this.limit
      this.index_start = limit * (page - 1)

      const params = {
        p: String(page),
        index_start: String(this.index_start),
        limit: String(limit),
        order: this.selectedOrder || '',
        search: this.radarSearch.text || '',
        search_title: this.radarSearch.text.replace(/ {1,}/g, '&') || '',
        radius: this.radarSearch?.city?.formatted_locality && this.advancedFilters.radius ? String(this.advancedFilters.radius) : '',
        city: this.radarSearch?.city?.formatted_locality || '',
        lat: String(this.getLat || ''),
        lon: String(this.getLon || ''),
        country_iso_code: this.country_iso_code || '',
        orp: String(this.orp),
        verified: String(this.verified),
        ea: this.advancedFilters?.ea || '',
        skills: this.advancedFilters?.skills?.map(s => s?.id)?.toString() || '',
        contracts: this.advancedFilters?.contracts?.map(c => c.correlation_id)?.toString() || '',
        languages: this.advancedFilters?.languages?.map(c => c.correlation_id)?.toString() || '',
        seniorities: this.advancedFilters?.seniorities?.map(c => c.correlation_id)?.toString() || '',
        workingHours: this.advancedFilters?.workingHours?.map(c => c.correlation_id)?.toString() || '',
        remoteWorking: this.advancedFilters?.remoteWorking?.map(c => c.correlation_id)?.toString() || '',
        education: this.advancedFilters.education ? String(this.advancedFilters.education) : '',
        vertical: this.advancedFilters.vertical ? String(this.advancedFilters.vertical) : '',
        roles: this.advancedFilters?.roles?.map(r => r.id)?.toString() || '',
        benefits: this.advancedFilters?.benefits?.map(r => r.correlation_id)?.toString() || '',
        travel: this.advancedFilters?.travel ? String(this.advancedFilters?.travel) : '',
        protectedCategory: this.advancedFilters?.protectedCategory ? String(this.advancedFilters?.protectedCategory) : '',
        coid: this.$route.query.coid ? String(Number(this.$route.query.coid)) : ''

      }
      if (isFromFetch) {
        delete params.limit
        delete params.p
        delete params.verified
        delete params.search
      } else {
        delete params.limit
        delete params.lat
        delete params.lon
        delete params.country_iso_code
        delete params.search_title
      }
      Object.keys(params).forEach((key) => {
        const value = params[key]
        if (value === '' || value === undefined || value === null || (Array.isArray(value) && value?.length === 0)) {
          delete params[key]
        }
      })

      return params
    },
    async loadPage(currentPage) {
      this.showMain = !(!this.hasCampaign || this.$isEmptyObject(this.posts) || this.currentPage !== currentPage)
      const { params, query } = this.$route
      if (params.title) {
        this.radarSearch.text = params.title.replace(/-/g, ' ')
      }
      if (query.search) {
        this.radarSearch.text = query.search.toString().replace(/-/g, ' ')
      }
      if (this.getSearchTitle) {
        this.radarSearch.text = this.getSearchTitle.toString().replace(/-/g, ' ')
      }
      if (this.getCity || (params.city && !query.id)) {
        this.radarSearch.city = {
          formatted_locality: this.getCity
            ?.replace(/-/g, ' ')
            ?.replace(/\b\w/g, c => c.toUpperCase()) ||
      params.city
        ?.replace(/-/g, ' ')
        ?.replace(/\b\w/g, c => c.toUpperCase())
        }
        this.advancedFilters.city = this.radarSearch.city
        if (query.radius) {
          this.advancedFilters.radius = query.radius
        }
      }

      if (query.orp === 'true' || query.suggested === 'true') {
        this.orp = true
      }
      if (query.ea) {
        this.advancedFilters.ea = query.ea.toString()
      }
      if (query.verified !== undefined) {
        this.verified = query.verified === 'true'
      }
      if (query.order) {
        this.selectedOrder = query.order.toString()
      }
      if (query.vertical) {
        this.advancedFilters.vertical = query.vertical
      }
      if (query.education) {
        this.advancedFilters.education = query.education
      }
      if (query.travel) {
        this.advancedFilters.travel = query.travel
      }
      if (query.protectedCategory) {
        this.advancedFilters.protectedCategory = query.protectedCategory
      }
      const applyFilters = (filters, query, idType) => {
        filters.forEach(({ key, target }) => {
          if (query[key]) {
            const ids = query[key].toString().split(',')
            target.length = 0
            ids.forEach(id => target.push({ [idType]: id }))
          }
        })
      }
      applyFilters(
        [
          { key: 'contracts', target: this.advancedFilters.contracts },
          { key: 'workingHours', target: this.advancedFilters.workingHours },
          { key: 'languages', target: this.advancedFilters.languages },
          { key: 'benefits', target: this.advancedFilters.benefits },
          { key: 'seniorities', target: this.advancedFilters.seniorities },
          { key: 'remoteWorking', target: this.advancedFilters.remoteWorking }
        ],
        query,
        'correlation_id'
      )
      applyFilters(
        [
          { key: 'skills', target: this.advancedFilters.skills },
          { key: 'roles', target: this.advancedFilters.roles }
        ],
        query,
        'id'
      )

      this.selectedFilter = []

      const applyAdvancedFilters = (filters, type) => {
        filters.forEach((filter) => {
          const f = this.filters.find(f => Number(f.correlation_id) === Number(filter.correlation_id) && f.type === type)
          if (f) {
            this.selectedFilter.push(f)
          }
        })
      }

      if (this.advancedFilters.workingHours) {
        applyAdvancedFilters(this.advancedFilters.workingHours, 'workingHour')
      }

      if (this.advancedFilters.remoteWorking) {
        applyAdvancedFilters(this.advancedFilters.remoteWorking, 'remoteWorking')
      }

      if (this.orp) {
        const f = this.filters.find(f => f.type === 'orp')
        if (f) {
          this.selectedFilter.push(f)
        }
      }
      let newCampaigns = null

      if (!this.hasCampaign || this.$isEmptyObject(this.posts) || this.currentPage !== currentPage) {
        const params = this.sanitizeParams(true, currentPage)
        try {
          newCampaigns = await this.$axios.$get('/proxysbapi/radar-campaigns-v2', { params })
        } catch (error) {
          if (error && error.response && process.server) {
            if (error?.response?.status === 404) {
              this.$nuxt.error({ statusCode: error?.response?.status, message: error?.response?.message })
            } else if (error?.response?.status === 406) {
              this.$nuxt.error({ statusCode: 400, message: '' })
            } else {
              this.$nuxt.error({ statusCode: error?.response?.status, message: error?.response?.message })
            }
          } else if (error) {
            this.popupError = true
            this.error = {
              response: error?.response,
              message: error?.message,
              status: error.response?.status,
              stack: error?.stack
            }
          }
        }
      }
      let newPosts = newCampaigns
        ? { campaigns: newCampaigns.campaigns || [], limit: newCampaigns.limit || this.limit }
        : { campaigns: this.posts?.campaigns || [], limit: this.posts?.limit || this.limit }

      if (this.hasCampaign && this.isFirstEnter) {
        this.isFirstEnter = false
        newPosts = await this.updateFirstCampaign(newPosts)
      }
      this.limit = query.limit ? Number(query.limit) : newPosts.limit
      this.setPosts(newPosts)
      this.setCurrentPage(currentPage)
      if (this.radarSearch.text || this.radarSearch.city) {
        this.track(
          'search',
          this.radarSearch?.text,
          this.radarSearch?.city?.formatted_locality,
          this.advancedFilters.ea.toString(),
          this.orp,
          newCampaigns?.campaigns?.length
        )
      }

      // Questa parte viene sempre eseguita
      this.calculatePagination()
      this.showMain = true

      if (this.isMobile && this.campaign?.cid && typeof document !== 'undefined') {
        this.$nextTick(() => {
          const element = document.getElementById('campaign_' + this.campaign.cid)
          if (element) {
            element.scrollIntoView({ behavior: 'auto', block: 'start' })
          }
        })
      }
    },

    calculatePagination() {
      if (this.posts.campaigns && this.posts.campaigns.length) {
        this.pages = Math.ceil(this.posts.campaigns[0].count / this.limit)
        this.count = this.posts.campaigns[0].count
        const range = (start, stop, step) => Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + (i * step))
        if (this.pages < 5) {
          this.visiblePages = range(1, this.pages, 1)
        } else if (this.currentPage < this.pages - 4) {
          if (this.currentPage === 1) {
            this.visiblePages = range(this.currentPage, this.currentPage + 4, 1)
          } else if (this.currentPage === 2) {
            this.visiblePages = range(this.currentPage - 1, this.currentPage + 3, 1)
          } else {
            this.visiblePages = range(this.currentPage - 2, this.currentPage + 2, 1)
          }
        } else {
          this.visiblePages = range(this.pages - 4, this.pages, 1)
        }
      } else {
        this.pages = 1
        this.count = 0
      }
    }
  }
})

